import {Shift} from '@domain/shifthandover'
import {ApiClient} from '@hconnect/apiclient'
import {FilterTimeRange} from '@hconnect/common/types'
import {generateTimeRange} from '@hconnect/common/utils'
import {useUpmPlantTimezone, useUpmPlantNow} from '@hconnect/upm'
import {useQuery, UseQueryOptions} from '@tanstack/react-query'
import {PmNotificationFilterOptions, WorkOrdersPayload} from '@widgets/shift-event-filters'
import {AxiosError} from 'axios'
import {omit} from 'lodash'
import moment from 'moment-timezone'

import {
  Notification,
  NotificationPagedContainer,
  NotificationPagedContainerDto,
  NotificationDto
} from '../../types'
import {preparePayload} from '../../utils'

export const NotificationsQueryKey = 'work-orders-notifications'
export const NotificationQueryKey = 'work-orders-notification'

const mapNotifications = (
  data: NotificationPagedContainerDto,
  timezone: string
): NotificationPagedContainer => ({
  ...data,
  items: data.items.map((item) => ({
    ...item,
    createdAt: moment.utc(item.createdAt).tz(timezone)
  }))
})

export const useNotifications = (
  plantId: string,
  shifts: Shift[],
  {input, offset, limit}: WorkOrdersPayload<PmNotificationFilterOptions>,
  options?: Omit<UseQueryOptions<NotificationPagedContainer, AxiosError>, 'queryKey'>
) => {
  const timezone = useUpmPlantTimezone(plantId)
  const getPlantNow = useUpmPlantNow(plantId)
  const path = `/work-orders/${plantId}/notifications`

  return useQuery<NotificationPagedContainer, AxiosError>({
    queryKey: [NotificationsQueryKey, path, timezone, shifts, JSON.stringify(input), offset, limit],
    queryFn: async () => {
      const timeRange: FilterTimeRange = generateTimeRange(input.timeRange, shifts, getPlantNow)
      const filters = {
        ...omit(input, [
          'timeRange',
          'pmNotificationStatus',
          'sortNotificationsBy',
          'notifSortDir'
        ]),
        statuses: input.pmNotificationStatus,
        sortBy: input.sortNotificationsBy,
        sortingDirection: input.notifSortDir
      }
      const response = await ApiClient.axiosInstance.get<NotificationPagedContainerDto>(path, {
        params: {
          from: timeRange.startDate,
          to: timeRange.endDate,
          offset,
          limit,
          ...preparePayload<PmNotificationFilterOptions>(filters)
        }
      })
      return mapNotifications(response.data, timezone)
    },
    ...options
  })
}

type UseNotificationParams = {
  plantId: string
  notificationId?: string
  options?: Omit<UseQueryOptions<Notification | undefined, AxiosError>, 'queryKey'>
}

export const useNotification = ({plantId, notificationId, options}: UseNotificationParams) => {
  const timezone = useUpmPlantTimezone(plantId)
  const path = `/work-orders/${plantId}/notifications/${notificationId}`

  return useQuery<Notification | undefined, AxiosError>({
    queryKey: [NotificationQueryKey, path, timezone],
    queryFn: async () => {
      if (!notificationId) return undefined
      const response = await ApiClient.axiosInstance.get<NotificationDto>(path)
      return (
        response.data && {
          ...response.data,
          createdAt: moment.utc(response.data.createdAt).tz(timezone)
        }
      )
    },
    ...options
  })
}
